// Global tracker to prevent duplicate video loads across ALL component instances
// Shared between BackgroundVideo and S3FilePreview to prevent cross-component duplicates
// Key: canonical URL (without version params), Value: { timestamp, loading, loaded }
export const globalVideoLoadTracker = new Map<string, { timestamp: number; loading: boolean; loaded: boolean }>();

// Time window to consider a video as "recently loaded" (prevent duplicate loads)
export const VIDEO_LOAD_DEDUP_WINDOW_MS = 5000;

import { getCanonicalMediaUrl } from './url-normalization';

/**
 * Normalize URL to canonical form for deduplication
 * This ensures URLs with different query params map to the same cache key
 */
const getCanonicalUrl = (url: string): string => {
  return getCanonicalMediaUrl(url) || url;
};

// Helper to check if a video is already loading or was recently loaded
export const isVideoAlreadyHandled = (url: string): boolean => {
  // Normalize to canonical form for deduplication
  const canonicalUrl = getCanonicalUrl(url);
  const entry = globalVideoLoadTracker.get(canonicalUrl);
  if (!entry) return false;
  
  const now = Date.now();
  const isRecent = (now - entry.timestamp) < VIDEO_LOAD_DEDUP_WINDOW_MS;
  
  // If currently loading or recently loaded, skip
  if (entry.loading || (entry.loaded && isRecent)) {
    return true;
  }
  
  // Clean up old entries
  if (!isRecent) {
    globalVideoLoadTracker.delete(canonicalUrl);
  }
  
  return false;
};

// Mark a video as loading
export const markVideoLoading = (url: string): void => {
  // Normalize to canonical form for deduplication
  const canonicalUrl = getCanonicalUrl(url);
  globalVideoLoadTracker.set(canonicalUrl, {
    timestamp: Date.now(),
    loading: true,
    loaded: false,
  });
};

// Mark a video as loaded (successfully or with error)
export const markVideoComplete = (url: string, success: boolean): void => {
  // Normalize to canonical form for deduplication
  const canonicalUrl = getCanonicalUrl(url);
  globalVideoLoadTracker.set(canonicalUrl, {
    timestamp: Date.now(),
    loading: false,
    loaded: success,
  });
};

